import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import { StyledSwitch } from "../../components/Base";
import Button from "../../components/Button";

import {
  changeStatusAnswer,
  setAssessmentPassed,
  setPracticalStatus,
  updateStatusCertificate,
} from "../../api/training.deprecated";
import {
  getAssessmentPassed,
  getObservableByPositionId,
  getTrainingUser,
  isPracticalModuleCompleted,
  isTrainingCompleted,
  PassedObservable,
  ProcessStep,
} from "../../api/training";

function isObjectInArray(arr: any[], obj: any): boolean {
  for (let i = 0; i < arr.length; i++) {
    if (JSON.stringify(arr[i]) === JSON.stringify(obj)) {
      return true;
    }
  }
  return false;
}

const AssessmentTraining = () => {
  const { trainingUserId: trainingUserIdParam, positionId: positionIdParam } = useParams<{
    trainingUserId: string;
    positionId: string;
  }>();
  const history = useHistory();
  const goBack = () => history.goBack();
  const { t } = useTranslation();

  useEffect(() => {
    if (
      (trainingUserIdParam && isNaN(Number(trainingUserIdParam))) ||
      (positionIdParam && isNaN(Number(positionIdParam)))
    ) {
      goBack();
    }
  }, [trainingUserIdParam, positionIdParam]);

  const trainingUserId = parseInt(trainingUserIdParam, 10);
  const positionId = parseInt(positionIdParam, 10);

  const [observableList, setObservableList] = useState<ProcessStep[]>([]);
  const [refresh, setRefresh] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [rightAnswers, setRightAnswers] = useState(0);
  const [percentage, setPercentage] = useState(0);

  async function onStatusChange(
    trainingUserId: number,
    processStepId: number,
    activitiesTrainingId: number,
    observableId: number,
    passed?: boolean,
  ) {
    await changeStatusAnswer(trainingUserId, processStepId, activitiesTrainingId, observableId, passed ? 1 : 0);

    const assessmentPassed = await getAssessmentPassed(trainingUserId);
    const trainingUser = await getTrainingUser(trainingUserId);

    updateObservableListStatus(observableList, assessmentPassed);

    const { totalCount, rightCount, percentValue } = calculateAssessmentMetrics(observableList);

    setTotalQuestions(totalCount);
    setRightAnswers(rightCount);
    setPercentage(percentValue);

    await updateTrainingStatus(trainingUserId, percentValue, trainingUser);

    setRefresh(refresh * -1);
  }

  function updateObservableListStatus(observableList: ProcessStep[], assessmentPassed: PassedObservable[]) {
    observableList.forEach((elem) => {
      elem.ob.forEach((row) => {
        const item = {
          process_step_id: row.process_step_id,
          activities_training_id: row.activities_training_id,
          observable_id: row.observable_id,
        };

        row.passed = isObjectInArray(assessmentPassed, item);
      });
    });

    return observableList;
  }

  function calculateAssessmentMetrics(observableList: ProcessStep[]) {
    const { totalCount, rightCount } = observableList.reduce(
      (acc, elem) => {
        acc.totalCount += elem.ob.length;
        acc.rightCount += elem.ob.filter((row) => row.passed).length;
        return acc;
      },
      { totalCount: 0, rightCount: 0 },
    );

    const percentValue = totalCount === 0 ? 0 : Math.round((rightCount / totalCount) * 10000) / 100;
    return { totalCount, rightCount, percentValue };
  }

  async function updateTrainingStatus(trainingUserId: number, percentValue: number, trainingUser: any) {
    // TODO: move logic to the backend
    const isPassing = percentValue >= +process.env.REACT_APP_ASSESSMENT_PASSED!;
    await setAssessmentPassed(trainingUserId, isPassing ? 1 : 0);

    const practical_status = isPracticalModuleCompleted(
      {
        instructorComplete: trainingUser.Training.instructor_complete,
        assessorPassed: trainingUser.Training.assessor_passed,
        checkerPassed: trainingUser.Training.checker_passed,
      },
      {
        ...trainingUser,
        assessor_passed: isPassing ? 1 : 0,
      },
    )
      ? 1
      : 0;
    if (trainingUser.practical_status !== practical_status) {
      await setPracticalStatus(trainingUserId, practical_status);
      trainingUser.practical_status = practical_status;
    }

    const trainingCompleted = isTrainingCompleted(trainingUser.Training, trainingUser) ? 1 : 0;
    await updateStatusCertificate(trainingUser.user_id, trainingUser.Training.course_id, trainingCompleted);
  }

  useEffect(() => {
    (async () => {
      const res = await getObservableByPositionId(positionId!);
      const assessmentPassed = await getAssessmentPassed(trainingUserId);

      updateObservableListStatus(res, assessmentPassed);
      setObservableList(res);

      const { totalCount, rightCount, percentValue } = calculateAssessmentMetrics(res);

      setTotalQuestions(totalCount);
      setRightAnswers(rightCount);
      setPercentage(percentValue);
    })();
  }, [refresh, positionId, trainingUserId]);

  return (
    <>
      <Typography variant="h3">{t("assessment.title")}</Typography>
      <Typography variant="h4">
        {t("assessment.total_questions")}: {totalQuestions}
      </Typography>
      <Typography variant="h4">
        {t("assessment.passed_questions")}: {rightAnswers}
      </Typography>
      <Typography variant="h4">
        {t("assessment.percentage")}: {percentage} %
      </Typography>

      {observableList.map((item) => {
        return (
          <div key={item.process_step_id} style={{ paddingBottom: "30px", paddingTop: "30px" }}>
            <Typography variant="h4">{item.process_step_name}</Typography>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: "40%", fontSize: "1.2rem" }} align="left">
                      {t("assessment.activities")}
                    </TableCell>
                    <TableCell style={{ fontSize: "1.2rem" }} align="left">
                      {t("assessment.observable")}
                    </TableCell>
                    <TableCell style={{ width: "10%", fontSize: "1.2rem" }} align="left">
                      {t("assessment.passed")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {item.ob.map((row, i) => {
                    return (
                      <>
                        <TableRow
                          key={i}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell>{row.activities_training_name}</TableCell>
                          <TableCell>{row.observable_name}</TableCell>
                          <TableCell>
                            <StyledSwitch
                              title={t("assessment.switch_answer")}
                              checked={row.passed}
                              onChange={() =>
                                onStatusChange(
                                  trainingUserId,
                                  row.process_step_id,
                                  row.activities_training_id,
                                  row.observable_id,
                                  row.passed,
                                )
                              }
                            />
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        );
      })}

      <Button title={t("navigate_back")} type="button" onClick={() => goBack()}>
        {t("navigate_back")}
      </Button>
    </>
  );
};

export default AssessmentTraining;
